import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { fmt } from '@dcc-erp/shared';
import type { AccountType, FinStatementRow } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  useActiveCompanyId,
  useBalanceSheet,
  useBranches,
  useCashFlow,
  useDepartments,
  useIncomeStatement,
  useTrialBalance,
} from '../lib/queries';

import type { TrialBalanceGroupBy } from '../lib/queries';

// Dr-nature (ASSET/EXPENSE/COST) vs Cr-nature (others) shown in the TB row
// label. Mirrors natureFor() in apps/api/src/modules/ledger/ledger.service.ts.
// COST joined the Dr side in Phase 12 when COGS got its own type/range.
function isDrNature(t: AccountType): boolean {
  return t === 'ASSET' || t === 'EXPENSE' || t === 'COST';
}

// Wire decimal shape — Prisma serializes Decimal as string; the schemas also
// accept number, so normalize once for arithmetic and totalling.
function toNumber(v: string | number): number {
  return typeof v === 'string' ? Number(v) : v;
}

// 'YYYY-MM' from <input type="month"> -> 'YYYYMM' for the API.
function periodFromMonth(month: string): string {
  return month.length >= 7 ? month.slice(0, 4) + month.slice(5, 7) : '';
}

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Format a YYYY-MM-DD value (asOfDate) from a YYYYMM period — month-end.
function periodToMonthEnd(period: string): string {
  if (!/^\d{6}$/.test(period)) return '';
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(4, 6));
  // Day 0 of next month is the last day of this month (local-tz; the wire
  // value is treated as a date-only by the API).
  const last = new Date(year, month, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
}

function periodToFirstDay(period: string): string {
  if (!/^\d{6}$/.test(period)) return '';
  return `${period.slice(0, 4)}-${period.slice(4, 6)}-01`;
}

// One-month-back as YYYYMM. Wraps year boundary correctly.
function previousPeriod(period: string): string {
  if (!/^\d{6}$/.test(period)) return '';
  let year = Number(period.slice(0, 4));
  let month = Number(period.slice(4, 6)) - 1;
  if (month === 0) {
    month = 12;
    year -= 1;
  }
  return `${year}${String(month).padStart(2, '0')}`;
}

// Thai month label for the period selector and column headers.
const THAI_MONTHS = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
];

function periodLabelTh(period: string): string {
  if (!/^\d{6}$/.test(period)) return period;
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(4, 6));
  return `${THAI_MONTHS[month - 1]} ${year}`;
}

function periodEndLabelTh(period: string): string {
  if (!/^\d{6}$/.test(period)) return period;
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(4, 6));
  const last = new Date(year, month, 0).getDate();
  return `${last} ${THAI_MONTHS[month - 1]} ${year}`;
}

type TabKey = 'bs' | 'is' | 'cf' | 'tb';

// Local row shape FinTable understands — kept as the union the prototype uses
// so the existing render code (and its CSS classes) doesn't change.
interface LineRow {
  type?: undefined;
  code: string;
  label: string;
  val: number;
  prev: number;
  indent?: 1;
}
interface HeaderRow {
  type: 'header';
  label: string;
}
interface SubRow {
  type: 'sub';
  label: string;
  val?: number;
  prev?: number;
}
interface TotalRow {
  type: 'total';
  label: string;
  val: number;
  prev: number;
}
type FinRow = LineRow | HeaderRow | SubRow | TotalRow;

// Convert the wire-format FinStatementRow (kind = HEADER/SUB/LINE/TOTAL) into
// the prototype's FinRow union (type = header/sub/total or LINE w/o type).
function toFinRows(rows: readonly FinStatementRow[]): FinRow[] {
  return rows.map((r): FinRow => {
    const val = toNumber(r.val);
    const prev = toNumber(r.prev);
    switch (r.kind) {
      case 'HEADER':
        return { type: 'header', label: r.label };
      case 'SUB':
        return { type: 'sub', label: r.label, val, prev };
      case 'TOTAL':
        return { type: 'total', label: r.label, val, prev };
      case 'LINE':
      default:
        return {
          code: r.code ?? '',
          label: r.label,
          val,
          prev,
          ...(r.indent && r.indent > 0 ? { indent: 1 as const } : {}),
        };
    }
  });
}

export const Route = createFileRoute('/reports')({
  component: ReportsPage,
});

function ReportsPage() {
  const companyId = useActiveCompanyId();
  const [tab, setTab] = useState<TabKey>('bs');
  // Period state is shared across all three statement tabs. Defaults to the
  // current month; comparison = previous month.
  const [month, setMonth] = useState<string>(currentMonth());
  const period = useMemo(() => periodFromMonth(month), [month]);
  const comparePeriod = useMemo(() => previousPeriod(period), [period]);

  // Dimension filters (Phase 42-A4) shared across all tabs. '' = no filter.
  // When set, every statement recomputes restricted to that branch / cost
  // center. Wired into BS / IS / CF / TB below.
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const { data: branches = [] } = useBranches(companyId);
  const { data: departments = [] } = useDepartments(companyId);

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['bs', 'งบแสดงฐานะการเงิน'],
    ['is', 'งบกำไรขาดทุน'],
    ['cf', 'งบกระแสเงินสด'],
    ['tb', 'งบทดลอง (Trial Balance)'],
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['รายงานและภาษี', 'งบการเงิน']}
        title="งบการเงิน (Financial Reports)"
        actions={
          <>
            <HelpButton guide="reports" />
            <input
              className="input w-sm"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={{ width: 160 }}
            />
            <select
              className="select"
              style={{ width: 170 }}
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              title="สาขา"
            >
              <option value="">สาขา: ทั้งหมด</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} — {b.nameTh}
                </option>
              ))}
            </select>
            <select
              className="select"
              style={{ width: 170 }}
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              title="หน่วยงาน"
            >
              <option value="">หน่วยงาน: ทั้งหมด</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.code} — {d.nameTh}
                </option>
              ))}
            </select>
            <select className="select" defaultValue="prev">
              <option value="prev">เปรียบเทียบ: งวดก่อน</option>
              <option value="prev-year">ปีก่อน</option>
              <option value="budget">งบประมาณ</option>
            </select>
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-4): wire export */
              }}
            >
              <Icon name="print" size={14} /> พิมพ์
            </button>
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-4): wire export */
              }}
            >
              <Icon name="download" size={14} /> Excel
            </button>
          </>
        }
      />

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'bs' && (
        <BalanceSheet
          period={period}
          comparePeriod={comparePeriod}
          branchId={branchId}
          departmentId={departmentId}
        />
      )}
      {tab === 'is' && (
        <IncomeStatement
          period={period}
          comparePeriod={comparePeriod}
          branchId={branchId}
          departmentId={departmentId}
        />
      )}
      {tab === 'cf' && (
        <CashFlow
          period={period}
          comparePeriod={comparePeriod}
          branchId={branchId}
          departmentId={departmentId}
        />
      )}
      {tab === 'tb' && <TrialBalance branchId={branchId} departmentId={departmentId} />}
    </div>
  );
}

interface FinTableProps {
  rows: readonly FinRow[];
  currentLabel: string;
  prevLabel: string;
}

function FinTable({ rows, currentLabel, prevLabel }: FinTableProps) {
  return (
    <table className="tbl">
      <thead>
        <tr>
          <th style={{ width: 90 }}>รหัส</th>
          <th>รายการ</th>
          <th className="num" style={{ width: 160 }}>
            {currentLabel}
          </th>
          <th className="num" style={{ width: 160 }}>
            {prevLabel}
          </th>
          <th className="num" style={{ width: 110 }}>
            เปลี่ยนแปลง
          </th>
          <th className="num" style={{ width: 80 }}>
            %
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          if (r.type === 'header') {
            return (
              <tr key={i} className="section-header">
                <td colSpan={6}>{r.label}</td>
              </tr>
            );
          }
          if (r.type === 'sub') {
            const val = r.val;
            const prev = r.prev;
            if (val === undefined || prev === undefined) {
              return (
                <tr key={i} className="subtotal">
                  <td />
                  <td>{r.label}</td>
                  <td className="num tnum" />
                  <td className="num tnum" />
                  <td className="num tnum" />
                  <td className="num tnum" />
                </tr>
              );
            }
            const chg = val - prev;
            const pct = prev ? (chg / prev) * 100 : 0;
            const sign = chg >= 0 ? 'pos' : 'neg';
            const prefix = chg >= 0 ? '+' : '';
            return (
              <tr key={i} className="subtotal">
                <td />
                <td>{r.label}</td>
                <td className="num tnum">{fmt(val)}</td>
                <td className="num tnum">{fmt(prev)}</td>
                <td className={`num tnum ${sign}`}>
                  {prefix}
                  {fmt(chg)}
                </td>
                <td className={`num tnum ${sign}`}>
                  {prefix}
                  {pct.toFixed(1)}%
                </td>
              </tr>
            );
          }
          if (r.type === 'total') {
            const chg = r.val - r.prev;
            const pct = r.prev ? (chg / r.prev) * 100 : 0;
            const sign = chg >= 0 ? 'pos' : 'neg';
            const prefix = chg >= 0 ? '+' : '';
            return (
              <tr key={i} className="total">
                <td />
                <td>{r.label}</td>
                <td className="num tnum">{fmt(r.val)}</td>
                <td className="num tnum">{fmt(r.prev)}</td>
                <td className={`num tnum ${sign}`}>
                  {prefix}
                  {fmt(chg)}
                </td>
                <td className={`num tnum ${sign}`}>
                  {prefix}
                  {pct.toFixed(1)}%
                </td>
              </tr>
            );
          }
          const chg = r.val - r.prev;
          const pct = r.prev ? (chg / r.prev) * 100 : 0;
          const sign = chg >= 0 ? 'pos' : 'neg';
          const prefix = chg >= 0 ? '+' : '';
          return (
            <tr key={i}>
              <td className="code">{r.code}</td>
              <td className={r.indent ? `indent-${r.indent}` : ''}>{r.label}</td>
              <td className="num tnum">{fmt(r.val)}</td>
              <td className="num tnum muted">{fmt(r.prev)}</td>
              <td className={`num tnum ${sign}`}>
                {prefix}
                {fmt(chg)}
              </td>
              <td className={`num tnum ${sign}`}>
                {prefix}
                {pct.toFixed(1)}%
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function StatusRow({ message, kind }: { message: string; kind: 'loading' | 'error' | 'empty' }) {
  const color =
    kind === 'error'
      ? 'var(--negative)'
      : kind === 'empty'
        ? 'var(--text-muted)'
        : 'var(--text-muted)';
  return (
    <div className="muted" style={{ padding: 24, textAlign: 'center', color }}>
      {message}
    </div>
  );
}

interface StatementProps {
  period: string;
  comparePeriod: string;
  // Line-level dimension filters (Phase 42-A4). '' = no filter.
  branchId: string;
  departmentId: string;
}

function BalanceSheet({ period, comparePeriod, branchId, departmentId }: StatementProps) {
  const companyId = useActiveCompanyId();
  // Service derives asOf from `period` (last day of month); we still pass the
  // explicit compareAsOfDate so the comparison column lines up with previous
  // month-end regardless of how the user changes the selector.
  const compareAsOfDate = useMemo(() => periodToMonthEnd(comparePeriod), [comparePeriod]);
  const { data, isPending, error } = useBalanceSheet(
    companyId,
    period
      ? {
          period,
          ...(compareAsOfDate ? { compareAsOfDate } : {}),
          ...(branchId ? { branchId } : {}),
          ...(departmentId ? { departmentId } : {}),
        }
      : {},
    !!period,
  );

  const rows = useMemo(() => (data ? toFinRows(data.rows) : []), [data]);

  return (
    <div className="tbl-shell">
      <div className="tbl-wrap">
        {isPending ? (
          <StatusRow message="กำลังโหลด…" kind="loading" />
        ) : error ? (
          <StatusRow message={`โหลดล้มเหลว: ${error.message}`} kind="error" />
        ) : rows.length === 0 ? (
          <StatusRow message="ไม่มีรายการ" kind="empty" />
        ) : (
          <FinTable
            rows={rows}
            currentLabel={periodEndLabelTh(period)}
            prevLabel={periodEndLabelTh(comparePeriod)}
          />
        )}
      </div>
      <div className="tbl-footer">
        <span>หน่วย: บาท · งบรวมก่อนตรวจสอบ (Unaudited)</span>
        <span>จัดทำโดย: ฝ่ายบัญชี</span>
      </div>
    </div>
  );
}

function IncomeStatement({ period, comparePeriod, branchId, departmentId }: StatementProps) {
  const companyId = useActiveCompanyId();
  // Service expands `period` to [firstDay, lastDay]; we feed the comparison
  // window explicitly because the API doesn't expand `comparePeriod`.
  const compareRange = useMemo(
    () => ({
      compareFrom: periodToFirstDay(comparePeriod),
      compareTo: periodToMonthEnd(comparePeriod),
    }),
    [comparePeriod],
  );
  const { data, isPending, error } = useIncomeStatement(
    companyId,
    period
      ? {
          period,
          ...(compareRange.compareFrom ? { compareFrom: compareRange.compareFrom } : {}),
          ...(compareRange.compareTo ? { compareTo: compareRange.compareTo } : {}),
          ...(branchId ? { branchId } : {}),
          ...(departmentId ? { departmentId } : {}),
        }
      : {},
    !!period,
  );

  const rows = useMemo(() => (data ? toFinRows(data.rows) : []), [data]);

  return (
    <div className="tbl-shell">
      <div className="tbl-wrap">
        {isPending ? (
          <StatusRow message="กำลังโหลด…" kind="loading" />
        ) : error ? (
          <StatusRow message={`โหลดล้มเหลว: ${error.message}`} kind="error" />
        ) : rows.length === 0 ? (
          <StatusRow message="ไม่มีรายการ" kind="empty" />
        ) : (
          <FinTable
            rows={rows}
            currentLabel={periodLabelTh(period)}
            prevLabel={periodLabelTh(comparePeriod)}
          />
        )}
      </div>
      <div className="tbl-footer">
        <span>หน่วย: บาท · งวด {periodLabelTh(period)}</span>
      </div>
    </div>
  );
}

function CashFlow({ period, comparePeriod, branchId, departmentId }: StatementProps) {
  const companyId = useActiveCompanyId();
  const compareRange = useMemo(
    () => ({
      compareFrom: periodToFirstDay(comparePeriod),
      compareTo: periodToMonthEnd(comparePeriod),
    }),
    [comparePeriod],
  );
  const { data, isPending, error } = useCashFlow(
    companyId,
    period
      ? {
          period,
          ...(compareRange.compareFrom ? { compareFrom: compareRange.compareFrom } : {}),
          ...(compareRange.compareTo ? { compareTo: compareRange.compareTo } : {}),
          ...(branchId ? { branchId } : {}),
          ...(departmentId ? { departmentId } : {}),
        }
      : {},
    !!period,
  );

  const rows = useMemo(() => (data ? toFinRows(data.rows) : []), [data]);

  return (
    <div className="tbl-shell">
      <div className="tbl-wrap">
        {isPending ? (
          <StatusRow message="กำลังโหลด…" kind="loading" />
        ) : error ? (
          <StatusRow message={`โหลดล้มเหลว: ${error.message}`} kind="error" />
        ) : rows.length === 0 ? (
          <StatusRow message="ไม่มีรายการ" kind="empty" />
        ) : (
          <FinTable
            rows={rows}
            currentLabel={periodLabelTh(period)}
            prevLabel={periodLabelTh(comparePeriod)}
          />
        )}
      </div>
    </div>
  );
}

interface TrialBalanceProps {
  branchId: string;
  departmentId: string;
}

function TrialBalance({ branchId, departmentId }: TrialBalanceProps) {
  const companyId = useActiveCompanyId();
  const [month, setMonth] = useState<string>(currentMonth());
  const period = useMemo(() => periodFromMonth(month), [month]);
  // Grouping: 'account' (default), or split each account by branch / dept.
  const [groupBy, setGroupBy] = useState<TrialBalanceGroupBy>('account');
  const grouped = groupBy !== 'account';
  // Dimension label depends on the chosen grouping.
  const dimLabel = groupBy === 'account-branch' ? 'สาขา' : 'หน่วยงาน';

  // Period is always set (defaults to current month) but we still guard the
  // call — sending '' would 400 on the regex check, sending it without skips it.
  const { data, isPending, error } = useTrialBalance(
    companyId,
    period
      ? {
          period,
          groupBy,
          ...(branchId ? { branchId } : {}),
          ...(departmentId ? { departmentId } : {}),
        }
      : {},
  );

  // Total table columns: 6 base, +1 when a dimension column is shown.
  const colCount = grouped ? 7 : 6;

  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <label style={{ fontSize: 14, color: 'var(--text-muted)' }}>งวด (เดือน)</label>
        <input
          className="input w-sm"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{ width: 160 }}
        />
        <label style={{ fontSize: 14, color: 'var(--text-muted)' }}>จัดกลุ่ม</label>
        <select
          className="select"
          style={{ width: 200 }}
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as TrialBalanceGroupBy)}
        >
          <option value="account">ตามบัญชี</option>
          <option value="account-branch">ตามบัญชี + สาขา</option>
          <option value="account-department">ตามบัญชี + หน่วยงาน</option>
        </select>
        {data?.totals?.balanced === false && (
          <span className="badge negative">
            <span className="bdot" /> ไม่สมดุล Dr ≠ Cr
          </span>
        )}
        {data?.totals?.balanced === true && (
          <span className="badge positive">
            <Icon name="check" size={10} /> สมดุล Dr = Cr
          </span>
        )}
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 600 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 90 }}>รหัส</th>
              <th>ชื่อบัญชี</th>
              {grouped && <th style={{ width: 160 }}>{dimLabel}</th>}
              <th style={{ width: 110 }}>ประเภท</th>
              <th className="num" style={{ width: 150 }}>
                เดบิต (เคลื่อนไหว)
              </th>
              <th className="num" style={{ width: 150 }}>
                เครดิต (เคลื่อนไหว)
              </th>
              <th className="num" style={{ width: 180 }}>
                ยอดคงเหลือ
              </th>
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="muted"
                  style={{ padding: 24, textAlign: 'center' }}
                >
                  กำลังโหลด…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="muted"
                  style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                >
                  โหลดล้มเหลว: {error.message}
                </td>
              </tr>
            ) : !data || data.rows.length === 0 ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="muted"
                  style={{ padding: 24, textAlign: 'center' }}
                >
                  ไม่มีรายการเคลื่อนไหวในงวดนี้
                </td>
              </tr>
            ) : (
              data.rows.map((r, i) => {
                const isDr = isDrNature(r.type);
                const dr = toNumber(r.debit);
                const cr = toNumber(r.credit);
                const bal = toNumber(r.balance);
                // When grouped the key must include the dimension id (an account
                // appears once per dimension; null dims share 'unassigned').
                const rowKey = grouped
                  ? `${r.accountCode}-${r.dimensionId ?? 'none'}-${i}`
                  : r.accountCode;
                return (
                  <tr key={rowKey}>
                    <td className="code">{r.accountCode}</td>
                    <td>{r.accountName}</td>
                    {grouped && <td className="muted">{r.dimensionName ?? 'ไม่ระบุ'}</td>}
                    <td className="muted">{r.type}</td>
                    <td className="num tnum">{dr > 0 ? fmt(dr) : '—'}</td>
                    <td className="num tnum">{cr > 0 ? fmt(cr) : '—'}</td>
                    <td className="num tnum">
                      <b>{fmt(bal)}</b>{' '}
                      <span className="meta" style={{ marginLeft: 4 }}>
                        {isDr ? 'Dr' : 'Cr'}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
            {data && data.rows.length > 0 && (
              <tr className="total">
                <td colSpan={grouped ? 4 : 3}>รวม</td>
                <td className="num tnum">{fmt(toNumber(data.totals.debit))}</td>
                <td className="num tnum">{fmt(toNumber(data.totals.credit))}</td>
                <td
                  className="num tnum"
                  style={{
                    color: data.totals.balanced ? 'var(--positive)' : 'var(--negative)',
                    fontWeight: 600,
                  }}
                >
                  {data.totals.balanced ? '✓ สมดุล' : '✗ ไม่สมดุล'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="tbl-footer">
        <span>
          {data?.periodLabel
            ? `งวด ${data.periodLabel.slice(0, 4)}-${data.periodLabel.slice(4, 6)}`
            : '—'}{' '}
          · {data?.rows.length ?? 0} บัญชี
        </span>
      </div>
    </div>
  );
}
